export type PersonaScriptsYesFilter = "all" | "yes";
export type PersonaScriptsSentStatus = "all" | "sent" | "notSent";
import type { ScriptPlatformFilter } from "./script-platform.js";

export type PersonaScriptsDocumentKind = "both" | "scripts" | "briefs";

export interface PersonaScriptsListFilters {
  favoritesOnly: boolean;
  hasSketch: PersonaScriptsYesFilter;
  hasAnimatic: PersonaScriptsYesFilter;
  sentStatus: PersonaScriptsSentStatus;
  documentKind: PersonaScriptsDocumentKind;
  platform: ScriptPlatformFilter;
}

export const PERSONA_SCRIPTS_YES_FILTER_VALUES = ["all", "yes"] as const;
export const PERSONA_SCRIPTS_SENT_STATUS_VALUES = ["all", "sent", "notSent"] as const;
export const PERSONA_SCRIPTS_DOCUMENT_KIND_VALUES = ["both", "scripts", "briefs"] as const;
export const PERSONA_SCRIPTS_PLATFORM_VALUES = ["all", "meta", "shorts", "in_stream"] as const;

export const DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS: PersonaScriptsListFilters = {
  favoritesOnly: false,
  hasSketch: "all",
  hasAnimatic: "all",
  sentStatus: "all",
  documentKind: "both",
  platform: "all",
};

/**
 * Dedicated keys so Scripts-tab lenses do not collide with the angles tab's
 * `q` / `status`, or the storyboard deep-link `variant`.
 */
export const PERSONA_SCRIPTS_LIST_SEARCH_KEYS = {
  q: "scriptsQ",
  kind: "scriptsKind",
  sent: "scriptsSent",
  sketch: "scriptsSketch",
  animatic: "scriptsAnimatic",
  favorites: "scriptsFavorites",
  platform: "scriptsPlatform",
} as const;

export type PersonaScriptsListSearchParams = {
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.q]?: string;
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.kind]?: PersonaScriptsDocumentKind;
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sent]?: PersonaScriptsSentStatus;
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sketch]?: PersonaScriptsYesFilter;
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.animatic]?: PersonaScriptsYesFilter;
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.favorites]?: boolean;
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.platform]?: ScriptPlatformFilter;
};

export interface PersonaScriptsListSearch {
  search: string;
  filters: PersonaScriptsListFilters;
}

function readString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function readEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function readFavorites(value: unknown): boolean {
  return value === true || value === "true";
}

export function readPersonaScriptsListSearch(
  search: Record<string, unknown>,
): PersonaScriptsListSearch {
  return {
    search: readString(search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.q]),
    filters: {
      favoritesOnly: readFavorites(search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.favorites]),
      hasSketch: readEnum(
        search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sketch],
        PERSONA_SCRIPTS_YES_FILTER_VALUES,
        DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.hasSketch,
      ),
      hasAnimatic: readEnum(
        search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.animatic],
        PERSONA_SCRIPTS_YES_FILTER_VALUES,
        DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.hasAnimatic,
      ),
      sentStatus: readEnum(
        search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sent],
        PERSONA_SCRIPTS_SENT_STATUS_VALUES,
        DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.sentStatus,
      ),
      documentKind: readEnum(
        search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.kind],
        PERSONA_SCRIPTS_DOCUMENT_KIND_VALUES,
        DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.documentKind,
      ),
      platform: readEnum(
        search[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.platform],
        PERSONA_SCRIPTS_PLATFORM_VALUES,
        DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.platform,
      ),
    },
  };
}

/** Defaults become `undefined` so they drop out of the URL. */
export function toPersonaScriptsListSearchParams(
  search: string,
  filters: PersonaScriptsListFilters,
): PersonaScriptsListSearchParams {
  const trimmed = search.trim();
  return {
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.q]: trimmed ? search : undefined,
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.kind]:
      filters.documentKind === DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.documentKind
        ? undefined
        : filters.documentKind,
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sent]:
      filters.sentStatus === DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.sentStatus
        ? undefined
        : filters.sentStatus,
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sketch]:
      filters.hasSketch === DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.hasSketch
        ? undefined
        : filters.hasSketch,
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.animatic]:
      filters.hasAnimatic === DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.hasAnimatic
        ? undefined
        : filters.hasAnimatic,
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.favorites]: filters.favoritesOnly ? true : undefined,
    [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.platform]:
      filters.platform === DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS.platform
        ? undefined
        : filters.platform,
  };
}

export function personaScriptsListSearchSnapshot(
  search: string,
  filters: PersonaScriptsListFilters,
): string {
  return JSON.stringify(toPersonaScriptsListSearchParams(search, filters));
}
